// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React from 'react';
import { Box, useTheme } from '@mui/system';
import { useAppDispatch, useAppSelector } from 'Hooks';
import { PROJECTS_SECTION_TITLE } from 'Utils/constants';
import ProjectsControls from './ProjectsControls';
import stylesDef from './styles';
import {
  MarkOrderSortFilterTouchedAction, SetOrderSortFilterAction, SetSortFilterAction,
} from '../projectListActions';
import { SetProjectViewModeThunk } from '../projectListThunk';
import { selectEffectiveOrderSortFilter } from '../projectListSelector';
import { ORDER_SORT_FILTER_OPTIONS } from '../projectViewOptions';

/* eslint-disable react/require-default-props */
interface IProjectsSubheaderProps {
  // While a search is running the results caption takes the section title's place. The
  // controls stay: a search can still be narrowed by status, re-sorted, or switched to cards.
  title?: string;
}
/* eslint-enable react/require-default-props */

// AUT-6432: the dashboard's copy of the projects controls, bound to the store. The controls
// themselves live in ProjectsControls so a folder page can render the same row from its own
// state without the two drifting apart. Changing the filter is a state change and nothing
// more: useDashboardProjects refetches the set the new filter describes, and the sort is a
// client-side comparison over the set already loaded.
export const ProjectsSubheader = ({ title }: IProjectsSubheaderProps) => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();
  const { sortFilter, projectViewMode } = useAppSelector((state) => state.projectList);
  const effectiveOrderSortFilter = useAppSelector(selectEffectiveOrderSortFilter);

  // Mark the filter touched the instant the dropdown opens, not just on a change. MUI's Select
  // skips onChange when the clicked item's value equals the currently-bound one, so marking on
  // open resolves the untouched-default override before the menu is interactive - otherwise a
  // real click on the option matching the default would be swallowed as a no-op.
  const handleOrderSortFilterOpen = () => {
    dispatch(MarkOrderSortFilterTouchedAction());
  };

  return (
    <Box sx={styles.stickyBand}>
      <ProjectsControls
        title={title ?? PROJECTS_SECTION_TITLE[effectiveOrderSortFilter]}
        sortFilter={sortFilter}
        onSortChange={(value) => dispatch(SetSortFilterAction(value))}
        orderFilter={effectiveOrderSortFilter}
        onOrderFilterChange={(value) => {
          dispatch(SetOrderSortFilterAction(value));
          dispatch(MarkOrderSortFilterTouchedAction());
        }}
        onOrderFilterOpen={handleOrderSortFilterOpen}
        filterOptions={ORDER_SORT_FILTER_OPTIONS}
        viewMode={projectViewMode}
        onViewModeChange={(mode) => dispatch(SetProjectViewModeThunk(mode))}
      />
    </Box>
  );
};

export default ProjectsSubheader;
